import React, { useCallback, useEffect } from 'react';
import { useToast } from '../../components/common/Toast';
import { useAuthStore } from '../../store/authStore';
import { useLanguage } from '../language/useLanguage';
import { ThemeContext } from './ThemeContext';
import { useThemePreferences } from './preferences/useThemePreferences';

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { user } = useAuthStore();
  const { showToast } = useToast();
  const { t } = useLanguage();
  const handleSyncError = useCallback(() => {
    showToast(t('dashboard.syncThemeWarning'), 'warning');
  }, [showToast, t]);
  const themeState = useThemePreferences({
    userId: user?.id,
    onSyncError: handleSyncError,
  });
  const { retryPendingSync } = themeState;

  useEffect(() => {
    if (!user?.id) {
      return;
    }
    retryPendingSync();
  }, [retryPendingSync, user?.id]);

  useEffect(() => {
    const handleOnline = () => {
      retryPendingSync();
    };
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [retryPendingSync]);

  return <ThemeContext.Provider value={themeState}>{children}</ThemeContext.Provider>;
};
